import { useEffect, useRef } from "react";

import type { AnalyticsBrowser } from "@segment/analytics-next";

import { useSite } from "~/contexts";
import { bySiteId } from "~/hec/utils/site-id";
import { updatePzHitsCuid } from "~/lib/abtasty-client-hits.client";
import { useRootLoaderData } from "~/root";

declare global {
  interface Window {
    analytics?: AnalyticsBrowser;
    truste?: {
      cma: {
        callApi: (
          action: string,
          domain: string,
        ) => { source: string; consentDecision: number[] };
      };
    };
  }
}

const BUCKET = {
  FUNCTIONAL: 2,
  OTHERADVERTISING: 3,
  ADVERTISINGWITHSENSITIVEDATA: 4,
} as const;

type TrustArcDecision = {
  source: string;
  consentDecision: number[];
};

function getTrustArcDecision(): TrustArcDecision | undefined {
  return window.truste?.cma?.callApi(
    "getGDPRConsentDecision",
    window.location.hostname,
  );
}

function bucketAllowed(
  decision: TrustArcDecision | undefined,
  bucket: number,
  fallback: boolean,
): boolean {
  if (!decision || decision.source === "implied") return fallback;
  return decision.consentDecision.includes(bucket);
}

async function loadSegment(
  writeKey: string,
  siteId: string,
  hashedEmail: string | null,
) {
  // COM-174: the Segment SDK (~90KB gz across analytics-next/-core/facade) is
  // dynamic-imported so it ships as its own async chunk instead of riding the
  // root chunk's critical path. The SDK was already client-effect-initialized,
  // so nothing observable moves earlier or later except the chunk fetch; the
  // load -> identify -> `segment:ready` ordering below is preserved verbatim.
  // Every consumer already gates on `waitForAnalytics()` / `segment:ready`.
  const { AnalyticsBrowser } = await import("@segment/analytics-next");
  const decision = getTrustArcDecision();
  const functional = bucketAllowed(decision, BUCKET.FUNCTIONAL, true);
  const advertising = bucketAllowed(decision, BUCKET.OTHERADVERTISING, false);
  const sensitiveAdvertising = bucketAllowed(
    decision,
    BUCKET.ADVERTISINGWITHSENSITIVEDATA,
    false,
  );

  // Integration names are Segment-workspace-specific. FSA uses "(FSA)"-suffixed destination
  // names. HSA workspace destination names are pending confirmation — TODO: ANALYTICS-28.
  // WDH has no Segment write key so loadSegment is never called for it, but bySiteId
  // requires exhaustive coverage.
  const integrations = bySiteId(siteId, {
    FSASTORE: {
      "Google Analytics": functional,
      "Facebook Pixel": advertising,
      "Google Ads Remarketing Lists (FSA)": advertising,
      "Personas Facebook Custom Audiences (FSA)": sensitiveAdvertising,
      // SFCC-12026: Bloomreach is a FUNCTIONAL destination (permit 2), not
      // advertising — so it is granted for the implied-consent majority
      // (advertising defaults false on implied, which silently dropped every
      // Bloomreach event and broke triggered flows). Matches SFCC-11124's
      // intended mapping and composable's live default-open posture. Both the
      // current ("Bloomreach Engagement") and legacy ("Exponea") Segment
      // destination names are set so whichever the FSA workspace uses is covered.
      "Bloomreach Engagement": functional,
      Exponea: functional,
      "Google Analytics 4 Web": functional,
      "Google Analytics 4 Cloud": functional,
      "Facebook Conversions API (Actions)": advertising,
      "Google Tag Manager": false,
      "Segment.io": true,
    },
    HSASTORE: {
      // HSA: confirm destination names with Segment workspace before adding — TODO: ANALYTICS-28
      "Google Tag Manager": false,
      "Segment.io": true,
    },
    WDH: {
      "Segment.io": false,
    },
  });

  window.analytics = AnalyticsBrowser.load({ writeKey }, { integrations });
  // D6 hit-sender identity belt (auth-transition audit V3, PR #1084 review):
  // reset (logout) clears the sender's cuid synchronously — `reset` emits
  // inline from `Analytics.reset()`, so it is ordering-safe. Deliberately NO
  // `identify` listener: identify emits only after ASYNC dispatch, so an
  // in-flight identify completing after a logout reset would resurrect the
  // former customer's cuid. The cuid is instead driven by the root loader's
  // session `hashedEmail` (AbTastyPageviewTracker mirror) — authoritative
  // session state, no event ordering to guard. Wired here, not once at
  // mount: a TrustArc re-init replaces `window.analytics` with a fresh
  // instance, dropping listeners on the old one. `on` is queue-safe pre-load
  // (AnalyticsBrowser buffers it); updatePzHitsCuid no-ops when the sender
  // is off.
  window.analytics.on("reset", () => updatePzHitsCuid(null));
  // SFCC-12026 (identity-first): bind the hard-ID (sha256 lowercased email,
  // server-computed in the root loader) synchronously right after load and
  // BEFORE dispatching `segment:ready`. Every ecommerce/page tracker gates on
  // `waitForAnalytics()` (resolves on this event), so enqueuing identify here
  // guarantees `ajs_user_id` is set before any track ships — otherwise those
  // tracks raced <SegmentIdentify />'s async effect and landed as soft-ID /
  // anonymous profiles that Bloomreach can't email. Anonymous shoppers
  // (no hashedEmail) stay soft-ID, as intended. <SegmentIdentify /> still fires
  // the enriched-traits identify (loyalty, persona); its dedup tolerates this.
  if (hashedEmail) void window.analytics.identify(hashedEmail);
  window.dispatchEvent(new CustomEvent("segment:ready"));
}

export const Segment = () => {
  const { segmentWriteKey, hashedEmail } = useRootLoaderData();
  const { site } = useSite();

  // Read via a ref so a mid-session login (hashedEmail change) does NOT re-run
  // the effect and tear down + reload the whole SDK — <SegmentIdentify /> owns
  // the post-login identify. loadSegment reads the latest value on the initial
  // load and on any TrustArc `submit_preferences` re-init.
  const hashedEmailRef = useRef(hashedEmail);
  hashedEmailRef.current = hashedEmail;

  useEffect(() => {
    if (!segmentWriteKey) return;

    // Attach message listener BEFORE starting the poll so submit_preferences
    // can't fire in the gap between poll creation and listener attachment.
    const onMessage = (e: MessageEvent) => {
      if (typeof e.data !== "string") return;
      try {
        const msg = JSON.parse(e.data);
        if (msg?.message === "submit_preferences") {
          // Re-init the SDK with the new destination-consent decision. Segment
          // itself is first-party; TrustArc only changes downstream integrations.
          // Fail-soft: analytics must never take the page down (chunk fetch
          // can fail offline / behind blockers).
          loadSegment(segmentWriteKey, site.id, hashedEmailRef.current).catch(
            error => console.error("Segment SDK load failed", error),
          );
        }
      } catch {
        /* not a TrustArc message */
      }
    };
    window.addEventListener("message", onMessage);

    loadSegment(segmentWriteKey, site.id, hashedEmailRef.current).catch(error =>
      console.error("Segment SDK load failed", error),
    );

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [segmentWriteKey, site.id]);

  return null;
};
